/* FLOURISHING FRAGRANCES — LAYERING LAB | HORIZONTAL V10
   Personalized layering partner suggestions and manual pairing.
   Reads existing escaped Master Scent attributes; no customer tracking or external APIs.
   Suggested combinations have NOT been tested unless explicitly recorded as such.
*/
(() => {
  'use strict';
  const $ = (root, selector) => root.querySelector(selector);
  const $$ = (root, selector) => Array.from(root.querySelectorAll(selector));
  const make = (tag, className, value) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (value !== undefined) el.textContent = String(value);
    return el;
  };
  const normal = value => String(value || '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const safeColor = value => /^#[0-9a-f]{3,8}$/i.test(String(value || '')) ? String(value) : '#d8d8d8';
  function readValues(raw) {
    let value = raw;
    try { value = JSON.parse(raw || 'null'); } catch (_) { /* regular text */ }
    if (value === null || value === undefined) return [];
    const items = Array.isArray(value) ? value : [value];
    return items.flatMap(item => {
      if (item && typeof item === 'object') return String(item.value || item.name || '').split(/[,;·\n]+/);
      return String(item || '').split(/[,;·\n]+/);
    }).map(v => v.trim()).filter(Boolean);
  }
  function prettyType(value) {
    const type = normal(value);
    return ({'single note':'Single Note','single notes':'Single Note','inspired by':'Inspired By','inspired':'Inspired By','original':'Original','fragrance free':'Fragrance Free'})[type] || value;
  }
  const concepts = {
    fruity: ['fruit', 'berry', 'berries', 'pear', 'peach', 'apple', 'cherry', 'lychee', 'dragonfruit', 'strawberry', 'raspberry', 'plum', 'tropical', 'mango', 'pineapple'],
    sweet: ['sweet', 'gourmand', 'vanilla', 'marshmallow', 'sugar', 'caramel', 'honey', 'candy', 'cream', 'creamy', 'chocolate', 'praline', 'tonka'],
    fresh: ['fresh', 'clean', 'aquatic', 'marine', 'ocean', 'ozonic', 'rain', 'citrus', 'lemon', 'lime', 'bergamot', 'grapefruit', 'mint', 'linen', 'eucalyptus', 'airy'],
    floral: ['floral', 'flower', 'rose', 'jasmine', 'peony', 'blossom', 'iris', 'orchid', 'gardenia', 'lily', 'violet', 'neroli'],
    warm: ['warm', 'cozy', 'amber', 'musk', 'musky', 'wood', 'woody', 'cedar', 'sandalwood', 'cashmere', 'spice', 'spicy', 'incense', 'patchouli', 'driftwood']
  };
  function hasPhrase(text, phrase) { return (' ' + normal(text) + ' ').includes(' ' + normal(phrase) + ' '); }
  function conceptSet(scent) {
    const desc = [scent.family, scent.profile, scent.vibe, ...notes(scent)].join(' ');
    return new Set(Object.keys(concepts).filter(key => concepts[key].some(word => hasPhrase(desc, word))));
  }
  function notes(scent) { return [scent.single, ...scent.top, ...scent.heart, ...scent.base].filter(Boolean); }
  function commonNotes(a, b) {
    const left = new Map(notes(a).map(n => [normal(n), n]).filter(([n]) => n));
    return [...new Set(notes(b).map(n => normal(n)).filter(n => n && left.has(n)))].map(n => left.get(n));
  }
  function readCatalog(root) {
    const seen = new Set();
    return $$(root, '[data-ffl-catalog] [data-ffl-scent]').map(el => ({
      id: el.dataset.id || '', name: el.dataset.name || '',
      pickerName: el.dataset.pickerName || el.dataset.name || '',
      color: safeColor(el.dataset.color), type: prettyType(el.dataset.type || ''),
      family: el.dataset.family || '', profile: el.dataset.profile || '',
      vibe: el.dataset.vibe || '', inspiration: el.dataset.inspiration || '',
      single: el.dataset.single || '', top: readValues(el.dataset.top),
      heart: readValues(el.dataset.heart), base: readValues(el.dataset.base),
      description: el.dataset.description || '', url: el.dataset.url || '',
      testedWith: readValues(el.dataset.testedWith)
    })).filter(s => {
      if (!s.id || !s.name || !s.url || seen.has(s.id)) return false;
      seen.add(s.id); return true;
    }).sort((a,b) => a.pickerName.localeCompare(b.pickerName,undefined,{sensitivity:'base'}));
  }
  function tested(a,b) {
    const match = (a,b) => a.testedWith.some(v => normal(v) === normal(b.name) || normal(v) === normal(b.id) || normal(v) === normal(b.pickerName));
    return match(a,b) || match(b,a);
  }
  function scorePair(a,b,goal) {
    const shared = commonNotes(a,b);
    const ac = conceptSet(a), bc = conceptSet(b);
    const commonCategories = [...ac].filter(c => bc.has(c));
    const complements = [];
    if ((ac.has('fruity') && bc.has('sweet')) || (bc.has('fruity') && ac.has('sweet'))) complements.push('fruity and sweet');
    if ((ac.has('floral') && bc.has('warm')) || (bc.has('floral') && ac.has('warm'))) complements.push('floral and warm');
    if ((ac.has('fresh') && bc.has('floral')) || (bc.has('fresh') && ac.has('floral'))) complements.push('fresh and floral');
    if ((ac.has('fresh') && bc.has('warm')) || (bc.has('fresh') && ac.has('warm'))) complements.push('fresh and warm');
    let score = shared.length * 7 + commonCategories.length * 3 + complements.length * 5;
    if (goal !== 'any') score += bc.has(goal) ? 20 : -4;
    if (tested(a,b)) score += 30;
    // A predictable alphabetical tie break is applied at sort time.
    return {score, shared, commonCategories, complements, isTested: tested(a,b)};
  }
  // Bring cited shared notes into the visible note boxes, even if they are base notes.
  function brief(scent, featured = []) {
    const seen=new Set();
    const ordered=[...featured,...notes(scent)];
    const vals=ordered.filter(n=>{const key=normal(n);if(!key||seen.has(key))return false;seen.add(key);return true;});
    return vals.length ? vals.slice(0,3).join(' · ') : ([scent.vibe,scent.family].filter(Boolean)[0] || 'Scent profile not listed');
  }
  function joining(values) {
    return values.length>1 ? values.slice(0,-1).join(', ')+' and '+values.at(-1) : (values[0]||'');
  }
  // Primary fragrance directions use the displayed fragrance family and profile first,
  // rather than treating every heart/base note as equally prominent.
  function primaryConcepts(scent) {
    let primary = [scent.family, scent.profile].filter(Boolean).join(' ');
    if (!primary.trim()) primary = [scent.single, ...scent.top.slice(0, 3)].join(' ');
    return new Set(Object.keys(concepts).filter(key => concepts[key].some(word => hasPhrase(primary, word))));
  }
  const directionWords = {fruity:'fruit-forward',sweet:'sweet',fresh:'fresh',floral:'floral',warm:'warm'};
  function characterOf(scent) {
    const main = [...primaryConcepts(scent)];
    const fallback = [...conceptSet(scent)];
    const chosen = main.length ? main : fallback;
    return chosen.length ? chosen.slice(0,2).map(c=>directionWords[c]).join(' and ') : 'distinctive';
  }
  function distinctNotes(a, b) {
    const seen = new Set(notes(a).map(normal));
    const distinct = [];
    notes(b).forEach(n=>{
      const key=normal(n);
      if (key && !seen.has(key) && key!==normal(b.name)) { distinct.push(n); seen.add(key); }
    });
    return distinct;
  }
  function shortReason(a,b,info,direction='any') {
    const shared=info.shared.slice(0,2);
    const addition=distinctNotes(a,b);
    const second=joining(addition.slice(0,2));
    const aCharacter=characterOf(a), bCharacter=characterOf(b);
    const newDirections=[...primaryConcepts(b)].filter(c=>!primaryConcepts(a).has(c));
    const emphasis=newDirections[0];
    const relatedNotes=emphasis?addition.filter(n=>concepts[emphasis].some(word=>hasPhrase(n,word))).slice(0,2):[];
    const nuance=joining(relatedNotes.length?relatedNotes:addition.slice(0,2));
    const selectedDirection=direction!=='any' && concepts[direction] && conceptSet(b).has(direction);
    if (selectedDirection && nuance) {
      const goals={sweet:'sweeter',fresh:'fresher',floral:'more floral',warm:'warmer'};
      return `${b.name} adds ${nuance}, offering a ${goals[direction]} direction to explore alongside ${a.name}.`;
    }
    if (shared.length) {
      const lead=shared.length===1
        ? `A shared note of ${shared[0]} connects these two scents.`
        : `Their shared ${joining(shared)} notes give the pairing a common thread.`;
      if (emphasis) return `${lead} ${b.name} introduces a ${directionWords[emphasis]} contrast${nuance?', with '+nuance:''}.`;
      if (second) return `${lead} ${b.name} adds ${second}, bringing another facet to their ${bCharacter} style.`;
      return `${lead} Their similar profiles offer a familiar starting point for layering.`;
    }
    if (emphasis) {
      return `${b.name} brings a ${directionWords[emphasis]} contrast to ${a.name}'s ${aCharacter} character${nuance?', with '+nuance:''}. It could make for an interesting layering experiment.`;
    }
    const common=[...primaryConcepts(a)].filter(c=>primaryConcepts(b).has(c));
    if (common.length && second) {
      return `Both scents have a ${directionWords[common[0]]} side, while ${b.name} introduces ${second}. It could put a new spin on a familiar fragrance style.`;
    }
    if (second) {
      return `The ${aCharacter} side of ${a.name} meets ${b.name}'s ${bCharacter} character, with notes such as ${second}. It's a different pairing direction to explore.`;
    }
    if(info.complements.length) return `Their ${aCharacter} and ${bCharacter} profiles suggest a contrasting combination worth exploring.`;
    return `These ${aCharacter} and ${bCharacter} fragrance profiles offer a combination to explore. Results will depend on the finished products.`;
  }
  function reason(a,b,info){return shortReason(a,b,info);}
  // Editorial names describe recorded scent characteristics, not an experimentally tested blend.
  function pairingStyle(a, b, info) {
    const ac = conceptSet(a), bc = conceptSet(b);
    const pairings = [
      ['fruity','sweet','Fruity + Sweet'], ['floral','warm','Floral + Cozy'],
      ['fresh','floral','Fresh + Floral'], ['fresh','warm','Fresh + Warm'],
      ['fruity','floral','Fruity + Floral'], ['sweet','warm','Sweet + Cozy']
    ];
    for (const [x,y,label] of pairings) {
      if ((ac.has(x) && bc.has(y)) || (bc.has(x) && ac.has(y))) return label;
    }
    const labels = {fruity:'Fruity',sweet:'Sweet',fresh:'Fresh',floral:'Floral',warm:'Warm'};
    if (info.commonCategories.length) return 'Shared ' + labels[info.commonCategories[0]] + ' Notes';
    const first = [...ac][0], second = [...bc][0];
    if (first && second) return labels[first] + ' + ' + labels[second];
    return info.shared.length ? 'Shared Notes' : 'A New Combination';
  }
  function init(root) {
    if (!root || root.dataset.fflReady === 'true') return;
    const q = selector => $(root, selector);
    const dialog=q('[data-ffl-dialog]'), list=q('[data-ffl-options]'), search=q('[data-ffl-search]');
    const open={first:q('[data-ffl-open="first"]'),second:q('[data-ffl-open="second"]')};
    const workspace=q('[data-ffl-workspace]'), start=q('.ffl__start'), choose=q('[data-ffl-choose]'), discovery=q('[data-ffl-discovery]');
    const results=q('[data-ffl-results]'), finder=q('[data-ffl-finder]'), manual=q('[data-ffl-manual]'), empty=q('[data-ffl-empty]');
    if (!dialog || !list || !search || !open.first || !open.second || !workspace || !choose || !discovery || !results || !finder || !manual || !empty) return;
    root.dataset.fflReady='true';
    const catalog=readCatalog(root), map=new Map(catalog.map(s=>[s.id,s]));
    const chosen={first:'',second:''};
    let mode='', goal='any', editing='', activeTab='all', activeFilter='';
    let returnKeyboard=false, nextScroll=null, nextFocus=null;
    const tabButtons=$$(root,'[data-ffl-tab]'), modeButtons=$$(root,'[data-ffl-mode]');
    const filters=q('[data-ffl-filters]'), count=q('[data-ffl-count]');
    const optionsEmpty=q('[data-ffl-no-results]'), swap=q('[data-ffl-swap]');
    const reducedMotion=()=>window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Keep each stage in the SAME workspace. Never scroll the webpage as the user progresses.
    // Move keyboard focus only when navigation originated on the keyboard.
    function scrollTo(el, focusTarget) {
      if (!el) return;
      window.requestAnimationFrame(()=>{
        if (focusTarget && !focusTarget.closest('[hidden]')) focusTarget.focus({preventScroll:true});
      });
    }
    function trigger(key) {
      const scent=map.get(chosen[key]);
      const name=q('[data-ffl-trigger-name="'+key+'"]'), meta=q('[data-ffl-trigger-meta="'+key+'"]'), dot=q('[data-ffl-trigger-dot="'+key+'"]');
      name.textContent=scent?scent.pickerName:'Select a scent';
      meta.textContent=scent?[scent.family,scent.profile].filter(Boolean).join(' · '):'';
      meta.hidden=!meta.textContent; dot.hidden=!scent;
      if(scent) dot.style.backgroundColor=scent.color;
    }
    function updateMode(next) {
      if (mode===next) return;
      mode=next;
      chosen.second=''; goal='any';
      const ref=q('[data-ffl-refine]'); if(ref)ref.open=false;
      $$(root,'[data-ffl-goal]').forEach(b=>{const selected=b.dataset.fflGoal==='any';b.classList.toggle('is-active',selected);b.setAttribute('aria-pressed',String(selected));});
      render();
    }
    function filterGroupValue(scent) {
      return activeTab==='profile'?scent.profile.trim():activeTab==='family'?scent.family.trim():activeTab==='type'?scent.type.trim():'';
    }
    function renderFilters() {
      filters.replaceChildren();
      if(activeTab==='all'){filters.hidden=true;return;}
      const values=[...new Set(catalog.map(filterGroupValue).filter(Boolean))].sort((a,b)=>a.localeCompare(b));
      filters.hidden=!values.length;
      if(activeFilter && !values.includes(activeFilter))activeFilter='';
      ['All',...values].forEach((name,i)=>{
        const val=i===0?'':name,button=make('button','ffl__picker-filter'+(val===activeFilter?' is-active':''),name);
        button.type='button'; button.setAttribute('aria-pressed',String(val===activeFilter));
        button.addEventListener('click',()=>{activeFilter=val;renderFilters();renderOptions();});filters.append(button);
      });
    }
    function renderOptions() {
      list.replaceChildren();
      const text=normal(search.value),other=editing==='first'?'second':'first';
      const shown=catalog.filter(s=>{
        if(activeFilter&&filterGroupValue(s)!==activeFilter)return false;
        return !text || normal([s.name,s.pickerName,s.family,s.profile,s.type,s.vibe,s.inspiration,s.description,...notes(s)].join(' ')).includes(text);
      });
      count.textContent=shown.length+(shown.length===1?' scent':' scents');
      optionsEmpty.hidden=shown.length>0;
      shown.forEach(s=>{
        const selected=s.id===chosen[editing], used=mode==='manual'&&s.id===chosen[other];
        const row=make('button','ffl__picker-item'+(selected?' is-selected':''));
        row.type='button';row.disabled=used;
        row.setAttribute('aria-pressed',String(selected));
        const dot=make('span','ffl__picker-item-dot');dot.style.backgroundColor=s.color;dot.setAttribute('aria-hidden','true');
        const copy=make('span','ffl__picker-item-copy');copy.append(make('span','ffl__picker-item-name',s.pickerName));
        const meta=[s.profile,s.family,s.type].filter(Boolean).join(' · ');
        if(meta)copy.append(make('span','ffl__picker-item-meta',meta));
        if(s.inspiration)copy.append(make('span','ffl__picker-item-inspiration','Inspiration: '+s.inspiration));
        const marker=make('span','ffl__picker-item-marker',used?'Your first scent':selected?'Selected':'');
        row.append(dot,copy,marker);
        row.addEventListener('click',event=>{
          const which=editing;
          chosen[which]=s.id;
          if(which==='first') chosen.second='';
          render();
          // After choosing a scent, move attention to the next meaningful step.
          const hasPair=!!(map.get(chosen.first)&&map.get(chosen.second));
          if(hasPair){nextScroll=results;nextFocus=event.detail===0?q('[data-ffl-pair-title]'):null;}
          else if(which==='first'&&!mode){nextScroll=choose;nextFocus=event.detail===0?modeButtons[0]:null;}
          else if(which==='first'&&mode){nextScroll=discovery;nextFocus=event.detail===0?(mode==='manual'?open.second:q('[data-ffl-refine] summary')):null;}
          else {nextScroll=discovery;nextFocus=null;}
          dialog.close();
        });
        list.append(row);
      });
    }
    function openPicker(which) {
      if(which==='second'&&mode!=='manual') return;
      editing=which;
      q('[data-ffl-picker-context]').textContent=which==='first'?'FIRST SCENT':'SECOND SCENT';
      search.value=''; activeTab='all'; activeFilter='';
      tabButtons.forEach(b=>{const active=b.dataset.fflTab==='all';b.classList.toggle('is-active',active);b.setAttribute('aria-pressed',String(active));});
      renderFilters(); renderOptions();
      if(typeof dialog.showModal==='function')dialog.showModal();
      open[which].setAttribute('aria-expanded','true');
    }
    function renderPair() {
      const a=map.get(chosen.first),b=map.get(chosen.second);
      if(!a||!b||a.id===b.id)return;
      const info=scorePair(a,b,'any');
      q('[data-ffl-pair-title]').textContent=a.name+' + '+b.name;
      q('[data-ffl-pair-vibe]').textContent=pairingStyle(a,b,info)+' · Suggested from scent notes';
      q('[data-ffl-why]').textContent=reason(a,b,info);
      ['first','second'].forEach(k=>{
        const scent=k==='first'?a:b;
        q('[data-ffl-contrib-name="'+k+'"]').textContent=scent.name;
        q('[data-ffl-contrib-text="'+k+'"]').textContent=brief(scent, info.shared);
        q('[data-ffl-pair-dot="'+k+'"]').style.backgroundColor=scent.color;
        const link=q('[data-ffl-link="'+k+'"]');
        link.href=scent.url;
        link.target='_blank';
        link.rel='noopener noreferrer';
        link.textContent='Explore '+scent.name;
        link.setAttribute('aria-label','Explore '+scent.name+' in Shop by Scent (opens in a new tab)');
      });
      q('[data-ffl-tested]').hidden=!info.isTested;
    }
    function partnerCandidates() {
      const source=map.get(chosen.first);
      if (!source) return {options:[],fallback:false};
      const primary=primaryConcepts(source);
      const candidates=catalog.filter(s=>s.id!==source.id).map(s=>{
        const info=scorePair(source,s,goal),signature=primaryConcepts(s);
        const added=[...signature].filter(c=>!primary.has(c));
        const sharedPrimary=[...signature].filter(c=>primary.has(c));
        const union=new Set([...signature,...primary]);
        const similarity=union.size ? sharedPrimary.length/union.size : 1;
        const evidence=info.isTested||info.shared.length>0||info.complements.length>0||info.commonCategories.length>0;
        return {s,info,signature,added,similarity,evidence};
      });
      const directed=goal==='any'?candidates:candidates.filter(x=>conceptSet(x.s).has(goal));
      const fallback=goal!=='any'&&!directed.length;
      const pool=(fallback?candidates:directed).filter(x=>x.evidence)
        .sort((x,y)=>y.info.score-x.info.score||x.s.name.localeCompare(y.s.name));
      const selected=[];
      const used=x=>selected.some(y=>y.s.id===x.s.id);
      const add=(x,role)=>{if (!x||used(x))return false;selected.push({...x,role});return true;};
      // Closest requires real shared notes, similar scent directions or a recorded test.
      const closest=pool.find(x=>x.info.isTested||x.info.shared.length||x.info.commonCategories.length>=2);
      if (closest) add(closest,'Closest match');
      // Complement: at least one contrasting *primary* direction and a recognizable
      // note/profile-based reason for considering the two scents together.
      const complement=pool.filter(x=>!used(x)&&x.added.length>0&&x.info.complements.length>0)
        .sort((x,y)=>y.added.length-x.added.length||y.info.score-x.info.score)[0];
      add(complement,'Complementary match');
      // Different direction must actually diverge from the starting scent AND from
      // suggestions already shown; never label a near-duplicate 'adventurous'.
      const different=pool.filter(x=>!used(x)&&x.added.length>0&&x.similarity<=0.5 &&
        (x.info.shared.length>0||x.info.complements.length>0||x.info.isTested) &&
        selected.every(y=>[...x.signature].some(c=>!y.signature.has(c))))
        .sort((x,y)=>x.similarity-y.similarity||y.added.length-x.added.length||y.info.score-x.info.score)[0];
      add(different,'Different direction');
      // If categories are too similar or data is incomplete, show fewer than three
      // honest suggestions. Never pad the list with a misleading 'Another idea'.
      if(!selected.length && pool.length) add(pool[0],'Notes-based idea');
      return {options:selected,fallback};
    }
    function renderPartners() {
      const source=map.get(chosen.first),box=q('[data-ffl-partner-list]');box.replaceChildren();
      const carousel=q('[data-ffl-carousel-controls]');
      if(!source)return;
      const {options,fallback}=partnerCandidates();
      q('[data-ffl-partner-count]').textContent=options.length+(options.length===1?' idea':' ideas');
      const note=q('[data-ffl-filter-note]');
      note.hidden=!!options.length&&!fallback;
      note.textContent=!options.length
        ? 'Not enough note or profile information to suggest a pairing confidently. Try another scent or create your own combination.'
        : fallback?'No direct matches for that direction yet. Here are the closest alternatives.':'';
      q('[data-ffl-filter-indicator]').hidden=goal==='any';
      carousel.hidden=options.length<2;
      q('[data-ffl-carousel-position]').textContent=options.length?'1 of '+options.length:'No suggestions';
      options.forEach(({s,info,role})=>{
        const card=make('article','ffl__partner');
        const identity=make('div','ffl__partner-identity');
        const dot=make('span','ffl__partner-dot');dot.style.backgroundColor=s.color;dot.setAttribute('aria-hidden','true');
        identity.append(dot,make('strong','ffl__partner-name',s.name));
        card.append(identity);
        card.append(make('p','ffl__partner-style',role));
        const meta=[pairingStyle(source,s,info),s.family,s.profile].filter(Boolean).join(' · ');
        if(meta)card.append(make('p','ffl__partner-meta',meta));
        card.append(make('p','ffl__partner-reason',shortReason(source,s,info,goal)));
        if(info.isTested)card.append(make('span','ffl__partner-tested','Recorded as tested'));
        const btn=make('button','ffl__partner-button','Explore pairing');btn.type='button';
        btn.setAttribute('aria-label','Explore '+source.name+' and '+s.name);
        btn.addEventListener('click',event=>{
          chosen.second=s.id;render();
          scrollTo(results,event.detail===0?q('[data-ffl-pair-title]'):null);
        });
        card.append(btn);box.append(card);
      });
    }
    // Mobile suggestion cards scroll horizontally within their own row, not the page.
    const partnerStrip=q('[data-ffl-partner-list]');
    let activeCard=0;
    function carouselCards(){return $$(partnerStrip,'.ffl__partner');}
    function currentCardIndex(){
      const cards=carouselCards(); if(!cards.length)return 0;
      const box=partnerStrip.getBoundingClientRect();
      let nearest=0, best=Infinity;
      cards.forEach((card,i)=>{
        const distance=Math.abs(card.getBoundingClientRect().left-box.left);
        if(distance<best){best=distance;nearest=i;}
      });
      return nearest;
    }
    function updateCarouselPosition(){
      const cards=carouselCards(); activeCard=currentCardIndex();
      q('[data-ffl-carousel-position]').textContent=cards.length?(activeCard+1)+' of '+cards.length:'No suggestions';
      q('[data-ffl-carousel-prev]').disabled=activeCard<=0;
      q('[data-ffl-carousel-next]').disabled=activeCard>=cards.length-1;
    }
    function moveCarousel(direction){
      const cards=carouselCards();if(!cards.length)return;
      const next=Math.max(0,Math.min(cards.length-1,currentCardIndex()+direction));
      partnerStrip.scrollTo({left:cards[next].offsetLeft-cards[0].offsetLeft,behavior:reducedMotion()?'auto':'smooth'});
      activeCard=next;
      q('[data-ffl-carousel-position]').textContent=(next+1)+' of '+cards.length;
      q('[data-ffl-carousel-prev]').disabled=next===0;
      q('[data-ffl-carousel-next]').disabled=next===cards.length-1;
    }
    q('[data-ffl-carousel-prev]').addEventListener('click',()=>moveCarousel(-1));
    q('[data-ffl-carousel-next]').addEventListener('click',()=>moveCarousel(1));
    partnerStrip.addEventListener('scroll',()=>window.requestAnimationFrame(updateCarouselPosition),{passive:true});
    function render() {
      trigger('first');trigger('second');
      const first=map.get(chosen.first), second=map.get(chosen.second);
      const hasPair=!!(mode&&first&&second&&first.id!==second.id);
      root.dataset.fflStage=hasPair?'pair':mode?'discover':first?'choose':'start';
      root.dataset.fflMode=mode||'none';
      workspace.hidden=hasPair;
      start.hidden=!!first;
      choose.hidden=!first||!!mode;
      discovery.hidden=!first||!mode;
      // The progress bar stays in one place while the active panel changes beneath it.
      const activeStep=hasPair?'discover':mode?'discover':first?'choose':'start';
      $$(root,'[data-ffl-step-nav]').forEach(button=>{
        const step=button.dataset.fflStepNav;
        const enabled=step==='start'||(step==='choose'&&!!first)||(step==='discover'&&!!first&&!!mode);
        const current=step===activeStep;
        button.disabled=!enabled;
        button.classList.toggle('is-current',current);
        button.classList.toggle('is-complete',enabled&&!current&&((step==='start'&&!!first)||(step==='choose'&&!!mode)));
        if(current)button.setAttribute('aria-current','step');else button.removeAttribute('aria-current');
      });
      const current=q('[data-ffl-current]');
      current.hidden=!first||!mode;
      if(first){
        q('[data-ffl-current-name]').textContent=first.name;
        q('[data-ffl-current-dot]').style.backgroundColor=first.color;
      }
      finder.hidden=mode!=='find'||!first;
      manual.hidden=mode!=='manual'||!first;
      results.hidden=!hasPair;
      modeButtons.forEach(b=>{const active=b.dataset.fflMode===mode;b.classList.toggle('is-active',active);b.setAttribute('aria-pressed',String(active));});
      empty.hidden=catalog.length>=2;
      if(!empty.hidden)empty.textContent='Layering suggestions need at least two scents linked to wearable body or fragrance products. Check the product variants’ custom.scent references.';
      if(mode==='find'&&first&&!hasPair){renderPartners();partnerStrip.scrollLeft=0;updateCarouselPosition();}
      swap.hidden=mode!=='manual';
      if(hasPair)renderPair();
      q('[data-ffl-change-partner]').textContent=mode==='manual'?'← Change second scent':'← Back to pairing ideas';
    }
    Object.entries(open).forEach(([key,button])=>button.addEventListener('click',()=>openPicker(key)));
    $$(root,'[data-ffl-step-nav]').forEach(button=>button.addEventListener('click',event=>{
      const step=button.dataset.fflStepNav;
      if(step==='start'){
        openPicker('first');
      } else if(step==='choose'&&map.has(chosen.first)){
        updateMode('');
        scrollTo(choose,event.detail===0?modeButtons[0]:null);
      } else if(step==='discover'&&mode){
        chosen.second='';render();
        scrollTo(discovery,event.detail===0?(mode==='manual'?open.second:q('[data-ffl-refine] summary')):null);
      }
    }));
    q('[data-ffl-close]').addEventListener('click',()=>dialog.close());
    dialog.addEventListener('click',event=>{if(event.target===dialog)dialog.close();});
    dialog.addEventListener('cancel',()=>{returnKeyboard=true;});
    dialog.addEventListener('close',()=>{
      Object.values(open).forEach(button=>button.setAttribute('aria-expanded','false'));
      const active=editing; editing='';
      const target=nextScroll, focus=nextFocus;nextScroll=null;nextFocus=null;
      if(target){scrollTo(target,focus);}
      else if(active&&open[active]&&!open[active].closest('[hidden]')){
        if(returnKeyboard)open[active].focus({preventScroll:true});else open[active].blur();
      }
      returnKeyboard=false;
    });
    q('[data-ffl-change-first]').addEventListener('click',()=>openPicker('first'));
    q('[data-ffl-change-mode]').addEventListener('click',event=>{
      updateMode('');
      scrollTo(choose,event.detail===0?modeButtons[0]:null);
    });
    search.addEventListener('input',renderOptions);
    tabButtons.forEach(b=>b.addEventListener('click',()=>{activeTab=b.dataset.fflTab;activeFilter='';tabButtons.forEach(t=>{const on=t===b;t.classList.toggle('is-active',on);t.setAttribute('aria-pressed',String(on));});renderFilters();renderOptions();}));
    modeButtons.forEach(b=>b.addEventListener('click',event=>{updateMode(b.dataset.fflMode);scrollTo(discovery,event.detail===0?(b.dataset.fflMode==='manual'?open.second:q('[data-ffl-refine] summary')):null);}));
    $$(root,'[data-ffl-goal]').forEach(b=>b.addEventListener('click',()=>{goal=b.dataset.fflGoal;$$(root,'[data-ffl-goal]').forEach(t=>{const on=t===b;t.classList.toggle('is-active',on);t.setAttribute('aria-pressed',String(on));});renderPartners();}));
    swap.addEventListener('click',()=>{[chosen.first,chosen.second]=[chosen.second,chosen.first];render();});
    q('[data-ffl-change-partner]').addEventListener('click',event=>{
      chosen.second='';render();
      scrollTo(discovery,event.detail===0?(mode==='manual'?open.second:q('[data-ffl-refine] summary')):null);
    });
    q('[data-ffl-reset]').addEventListener('click',event=>{
      chosen.first='';chosen.second='';mode='';goal='any';
      render();
      scrollTo(q('.ffl__start'),event.detail===0?open.first:null);
    });
    render();
    if(catalog.length<2)Object.values(open).forEach(button=>button.disabled=true);
  }
  function all(context) {
    if(context.matches?.('[data-ffl-root]'))init(context);
    context.querySelectorAll('[data-ffl-root]').forEach(init);
  }
  if(!window.__ffLayeringLabHorizontalV10){
    document.addEventListener('DOMContentLoaded',()=>all(document));
    document.addEventListener('shopify:section:load',event=>all(event.target));
    window.__ffLayeringLabHorizontalV10=true;
  }
  all(document);
})();
